import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, PawPrint, Flag, Loader2, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { ReportMessageModal } from './ReportMessageModal';
import { useTranslation } from 'react-i18next';

interface ChatWindowProps {
  chatRoomId: string;
  matchInfo: any;
  onClose: () => void;
}

interface Message {
  id: string;
  sender_user_id: string;
  message_text: string;
  sent_at: string;
  is_read: boolean;
}

export const ChatWindow = ({ chatRoomId, matchInfo, onClose }: ChatWindowProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reportMessageId, setReportMessageId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const otherUserPet = matchInfo.user_1_id === user?.id ? matchInfo.pet_2 : matchInfo.pet_1;

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('match-chat', {
        body: {
          action: 'fetch',
          chat_room_id: chatRoomId
        }
      });

      if (error) throw error;
      
      if (data.success) {
        setMessages(data.messages || []);
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: t('chatWindow.errors.loadMessages'),
        description: t('chatWindow.errors.loadMessagesDescription'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('match-chat', {
        body: {
          action: 'send',
          chat_room_id: chatRoomId,
          message_text: messageText
        }
      });

      if (error) throw error;

      if (data.success) {
        setMessages(prev => [...prev, data.message]);
        scrollToBottom();
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast({
        title: t('chatWindow.errors.sendMessage'),
        description: error.message || t('chatWindow.errors.sendMessageDescription'),
        variant: 'destructive'
      });
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Real-time subscription
  useEffect(() => {
    fetchMessages();

    // Subscribe to new messages
    const channel = supabase
      .channel(`chat:${chatRoomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'match_chat_messages',
          filter: `chat_room_id=eq.${chatRoomId}`
        },
        (payload) => {
          const newMsg = payload.new as Message;
          if (newMsg.sender_user_id !== user?.id) {
            setMessages(prev => [...prev, newMsg]);
            scrollToBottom();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatRoomId, user?.id]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl h-[600px] flex flex-col p-0">
          {/* Header */}
          <DialogHeader className="p-4 border-b flex-shrink-0">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {otherUserPet?.photo_url ? (
                  <AvatarImage src={otherUserPet.photo_url} alt={otherUserPet.name} />
                ) : (
                  <AvatarFallback className="bg-pink-100">
                    <PawPrint className="h-5 w-5 text-pink-500" />
                  </AvatarFallback>
                )}
              </Avatar>
              <DialogTitle className="flex-1">{t('chatWindow.title', { petName: otherUserPet?.name })}</DialogTitle>
            </div>
          </DialogHeader>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isOwn = msg.sender_user_id === user?.id;
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                        <div
                          className={`rounded-2xl px-4 py-2 ${
                            isOwn
                              ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm break-words">{msg.message_text}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(msg.sent_at), { addSuffix: true })}
                          </span>
                          {!isOwn && (
                            <button
                              onClick={() => setReportMessageId(msg.id)}
                              className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
                            >
                              <Flag className="h-3 w-3" />
                              {t('chatWindow.report')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={scrollRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t flex-shrink-0">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chatWindow.typePlaceholder')}
                disabled={sending}
                className="flex-1"
                maxLength={1000}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {newMessage.length}/1000 {t('chatWindow.characters')}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      {reportMessageId && (
        <ReportMessageModal
          messageId={reportMessageId}
          onClose={() => setReportMessageId(null)}
        />
      )}
    </>
  );
};

const MessageCircle = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);
